import trapFocus from '../../utils/trapFocus';
import { debounce } from 'throttle-debounce';
import { SETTINGS, CLASSES } from '../../utils/constants';

const initCollectionSearchFilters = () => {
  const filtersContainer = document.querySelector('[data-filters-container]');
  if (!filtersContainer) return;

  const productGridEl = document.querySelector('[data-product-grid]');
  const paginationEl = document.querySelector('[data-pagination]');
  const filterDrawer = filtersContainer.querySelector('[data-filter-drawer]');
  const openDrawerBtn = filtersContainer.querySelector('[data-open-filter-drawer]');
  const closeDrawerBtn = filtersContainer.querySelector('[data-close-filter-drawer-button]');
  const filterFormEl = filtersContainer.querySelector('[data-filter-form]');
  const filterDrawerSubmitBtn = filtersContainer.querySelector('[data-filters-submit]');
  const filterDrawerClearAllBtn = filtersContainer.querySelector('[data-filters-clear-all]');
  const sortByEl = filtersContainer.querySelector('[data-sort-by-select]');
  const resultsCount = filtersContainer.querySelector('[data-filters-results-count]');
  const activeFiltersContainerEl = filtersContainer.querySelector('[data-active-filters]');
  const activeFilterCount = filtersContainer.querySelector('[data-active-filter-count]');

  const sectionId = filterDrawer.dataset.sectionId;

  // declare renderActiveFilters function here to avoid hoisting issues
  // and to allow it to be used in the renderPage function
  let renderActiveFilters = () => {};

  const fetchResults = async (url) => {
    try {
      const response = await fetch(url);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      console.error('Error fetching filter results:', err);
    }
  };

  const renderProductGrid = (resultsDoc) => {
    if (!resultsDoc) return;

    const mappings = [
      { oldEl: productGridEl, newEl: '[data-product-grid]' },
      { oldEl: paginationEl, newEl: '[data-pagination]' },
      { oldEl: resultsCount, newEl: '[data-filters-results-count]' },
      { oldEl: activeFilterCount, newEl: '[data-active-filter-count]' },
    ];

    mappings.forEach(({ oldEl, newEl }) => {
      const newContent = resultsDoc.querySelector(newEl);
      if (oldEl && newContent) oldEl.innerHTML = newContent.innerHTML;
    });
  };

  const renderFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newFiltersSubmitBtn = resultsDoc.querySelector('[data-filters-submit]');
    if (newFiltersSubmitBtn) {
      filterDrawerSubmitBtn.innerHTML = newFiltersSubmitBtn.innerHTML;
    }
    const newFiltersFormEl = resultsDoc.querySelector('[data-filter-form]');
    if (newFiltersFormEl) {
      const newFilterEls = newFiltersFormEl.querySelectorAll('[data-facet-filter]');
      const existingFilterEls = filterFormEl.querySelectorAll('[data-facet-filter]');
      existingFilterEls.forEach(existingFilter => {
        const existingFilterId = existingFilter.id;
        const existingFilterInputEl = existingFilter.querySelector('input');
        const newFilter = [...newFilterEls].find(newFilter => newFilter.id === existingFilterId);
        if (newFilter) {
          const newFilterInputEl = newFilter.querySelector('input');
          existingFilterInputEl.disabled = newFilterInputEl.disabled;
          existingFilterInputEl.checked = newFilterInputEl.checked;
          existingFilterInputEl.value = newFilterInputEl.value;
        }
        else {
          existingFilterInputEl.disabled = true;
        }
      });
    }
  };

  const renderPage = async (url) => {
    try {
      const fetchUrl = sectionId ? `${url}&section_id=${sectionId}` : url;
      const resultsDoc = await fetchResults(fetchUrl);
      renderProductGrid(resultsDoc);
      renderActiveFilters(resultsDoc);
      renderFilters(resultsDoc);
    }
    catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const handleFilterLinkClick = async (e) => {
    e.preventDefault();
    const closestLink = e.target.closest('a');
    if (!closestLink || !closestLink.href) return;

    const urlHref = closestLink.href;
    window.history.pushState({}, '', urlHref);
    await renderPage(urlHref);
  };

  const setUpActiveFilterEventListeners = () => {
    activeFiltersContainerEl.addEventListener('click', (e) => {
      const activeFilterEl = e.target.closest('[data-active-filter]');
      if (activeFilterEl) {
        handleFilterLinkClick(e);
      }
    });
  };

  renderActiveFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newActiveFiltersEl = resultsDoc.querySelector('[data-active-filters]');
    if (!newActiveFiltersEl) return;

    activeFiltersContainerEl.innerHTML = newActiveFiltersEl.innerHTML;
  };

  const convertFormDataToUrl = () => {
    const formData = new FormData(filterFormEl);
    if (sortByEl) {
      formData.append('sort_by', sortByEl.value);
    }
    const params = new URLSearchParams(formData).toString();
    const url = new URL(window.location.href);
    url.search = params;
    return url;
  };

  const handleFormInput = async () => {
    const url = convertFormDataToUrl();
    if (!url) return;

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };

  const debouncedFormInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleFormInput);

  const closeFilterDrawer = () => {
    filterDrawer.classList.remove(CLASSES.TRANSLATE_X_0);
    setTimeout(() => {
      filterDrawer.close();
    }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
  };

  const handleCloseFilterDrawerClick = () => {
    closeFilterDrawer();
  };

  const handleFilterDrawerClick = e => {
    if (!filterDrawer) return;
    const isInDialog = filterDrawer.getBoundingClientRect().right > e.clientX;
    if (!isInDialog) {
      closeFilterDrawer();
    }
  };

  const openFilterDrawer = () => {
    filterDrawer.showModal();
    //uses requestAnimationFrame to ensure the class is added after the modal is displayed (on safari)
    //without this, the transition does not work correctly on Safari
    requestAnimationFrame(() => {
      filterDrawer.classList.add(CLASSES.TRANSLATE_X_0);
    });
    trapFocus(filterDrawer);
    filterDrawer.addEventListener('click', handleFilterDrawerClick);
    filterFormEl.addEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn.addEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.addEventListener('click', handleFilterLinkClick);
    closeDrawerBtn.addEventListener('click', handleCloseFilterDrawerClick);
  };

  openDrawerBtn.addEventListener('click', () => openFilterDrawer());

  sortByEl.addEventListener('change', handleFormInput);
  setUpActiveFilterEventListeners();

  filterDrawer.addEventListener('close', () => {
    filterDrawer.removeEventListener('click', handleFilterDrawerClick);
    filterFormEl.removeEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    closeDrawerBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.removeEventListener('click', handleFilterLinkClick);
    openDrawerBtn.focus();
  });
};

export default initCollectionSearchFilters;
